import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';

import { Recepciones } from '../Recepciones/models/recepciones';

@Injectable({
  providedIn: 'root',
})
export class RecepcionesService {
  private api: string = '/api/recepciones';
  
  constructor(private http: HttpClient){}

  /**
   * Recupera el listado completo de recepciones históricas del taller.
   */
  getRecepcionesList(): Observable<Recepciones[]> {
    return this.http.get<Recepciones[]>(this.api);
  } 

  /**
   * Despacha una nueva recepción empaquetando el JSON y los archivos binarios (fotos).
   */
  createRecepciones(recepciones: Recepciones, archivos: File[]): Observable<any> {
    const formData = new FormData();
    
    // Serializa el payload de la recepción; Spring Boot lo mapeará automáticamente
    formData.append('recepcion', JSON.stringify(recepciones));
    
    // Adjunta cada imagen seleccionada desde la tablet/móvil
    archivos.forEach(archivo => {
      formData.append('archivos', archivo);
    });

    return this.http.post(this.api, formData);
  }

  /**
   * Elimina una recepción física del taller y dispara en cascada el borrado de subtablas y archivos.
   */
  deleteRecepcionesById(id: number): Observable<any> {
    return this.http.delete(`${this.api}/${id}`);
  }

  /**
   * =========================================================================
   * 🚀 MODIFICACIÓN SAAS BLINDADA DE 360 GRADOS (SANA EL CHOQUE DE COPIAS DE RED)
   * SOLUCIÓN: Sobrecargamos los parámetros agregando 'archivos: File[] = []' por defecto.
   * Esto permite que tu modal flotante de edición lo invoque pasando solo el objeto,
   * pero mantiene la compatibilidad multiparte que exige tu RecepcionesController.java.
   * =========================================================================
   */
  updateRecepciones(recepciones: Recepciones, archivos: File[] = []): Observable<any> {
    const formData = new FormData();
    
    // Empaqueta el objeto modificado de 360 grados (cabeceros + 5 subtablas)
    formData.append('recepcion', JSON.stringify(recepciones));
    
    // Si el operador adjuntó fotos de rampa extras en el modal, se van en el cable
    if (archivos && archivos.length > 0) {
      archivos.forEach(archivo => {
        formData.append('archivos', archivo);
      });
    }

    // Apunta dinámicamente al ID relativo al mapping de Spring Boot
    return this.http.put(`${this.api}/${recepciones.id}`, formData);
  }

  /**
   * Recupera una recepción específica por su ID para edición o consulta de detalles.
   */
  getRecepciones(id: number): Observable<Recepciones> {
    return this.http.get<Recepciones>(`${this.api}/${id}`);
  }
  
  /**
   * Consulta el próximo folio atómico disponible controlado por la tabla f0002 del servidor.
   */
  getFolioTentativo(): Observable<string> {
    return this.http.get(`${this.api}/folio-tentativo`, { responseType: 'text' });
  }
  
  /**
   * Consume el catálogo de clientes corporativos aprobados y vigentes.
   */
  getClientesActivos(): Observable<any[]> {
    return this.http.get<any[]>(`${this.api}/clientes-activos`);
  }

  /**
   * Recupera las unidades vehiculares asociadas a un cliente mediante su AN8 indexado.
   * @param an8 Código numérico de 8 dígitos del cliente seleccionado.
   */
  getUnidadesPorCliente(an8: number): Observable<any[]> {
    return this.http.get<any[]>(`${this.api}/unidades-por-cliente/${an8}`);
  }

  /**
   * Consume el listado de asesores y técnicos operativos configurados en el sistema.
   */
  getAsesoresActivos(): Observable<any[]> {
    return this.http.get<any[]>(`${this.api}/asesores`);
  }

  /**
   * =========================================================================
   * ✍️ INTEGRACIÓN LEGAL: CONSULTAR CONTRATO DINÁMICO (UDC)
   * Recupera las cláusulas y tarifas personalizadas según el cliente y tipo de taller.
   * =========================================================================
   */
  getContratoConfiguracionLegal(idRecepcion: number, etapaFirma: string = 'RECEPCION'): Observable<any> {
    return this.http.get<any>('/api/contratos/configuracion-legal', {
      params: { 
        idRecepcion: idRecepcion.toString(),
        etapaFirma: etapaFirma // 🚀 Viaja como 'RECEPCION' o 'COTIZACION'
      }
    });
  }

  /**
   * =========================================================================
   * ✍️ INTEGRACIÓN LEGAL: DESPACHAR RÚBRICAS DUALES EN BASE64
   * Envía el payload unificado al FirmasController para guardar los PNG en disco.
   * =========================================================================
   */
  guardarFirmasConformidad(payload: {
    coId: string;
    idRecepcion: number;
    folioRecepcion: string;
    firmaClienteBase64: string;
    firmaAsesorBase64: string;
    etapaFirma: string; // 🚀 INYECTADO: 'RECEPCION' | 'COTIZACION' | 'ENTREGA'
  }): Observable<any> {
    return this.http.post('/api/firmas/guardar', payload);
  }

  getGiroTaller(): Observable<string> {
    return this.http.get(`${this.api}/giro-taller`, { responseType: 'text' });
  }

}